import {
  Fab,
  FabProps,
  Button as MuiButton,
  ButtonProps as MuiButtonProps,
  styled,
} from "@mui/material";
import * as React from "react";
import { GroupedNavigationContext } from "./GroupedNavigationContext";
import Icon from "./Icon";
import Tooltip from "./Tooltip";
import useIsOverflowing from "../core/useIsOverflowing";

export interface ButtonProps extends MuiButtonProps {
  customSize?: "tiny";
  endIcon?: string;
  fab?: boolean;
  fullWidth?: boolean;
  icon?: string;
  label?: string;
  target?: string;
}

const FabIcon = styled(Icon)({
  width: "100%",
});

const Label = styled("span")({
  overflow: "hidden",
  textOverflow: "ellipsis",
});

interface RootButtonProps {
  fullWidth?: boolean;
}

const RootButton = styled(MuiButton, {
  shouldForwardProp: (prop) => prop !== "fullWidth",
})<RootButtonProps>(({ fullWidth }) => ({
  gap: "0.4em",
  ...(fullWidth && {
    width: "100%",
  }),
}));

interface RootFabProps {
  tiny?: boolean;
}

const RootFab = styled(Fab, {
  shouldForwardProp: (prop) => prop !== "tiny",
})<RootFabProps>(({ tiny }) =>
  tiny
    ? {
        fontSize: 10,
        height: 16,
        minHeight: 16,
        minWidth: 16,
        width: 16,
        zIndex: 0,
      }
    : { zIndex: 0 }
);

const Button = React.forwardRef(
  (
    props: ButtonProps,
    buttonRef: React.ForwardedRef<HTMLButtonElement>
  ): JSX.Element => {
    const {
      children,
      color = "default",
      customSize,
      endIcon,
      fab,
      fullWidth,
      icon,
      label,
      tabIndex,
      variant = "contained",
      ...otherProps
    } = props;

    const { isOverflowing, ref: labelRef } = useIsOverflowing();
    const { groupControlsFocus } = React.useContext(GroupedNavigationContext);
    const touchStart = React.useRef<number>(0);

    // When long pressing a button, the overflow tooltip appears in ~700ms. We
    // prevent clicks after the tooltip appears.
    const onTouchFinish: React.TouchEventHandler = (event) => {
      if (Date.now() - touchStart.current > 700) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    let labelText: string | null = null;
    if (label) {
      labelText = label;
    } else if (children && typeof children === "string") {
      labelText = children;
    }

    const labelElement = labelText ? (
      <Label ref={labelRef}>{labelText}</Label>
    ) : (
      children
    );

    const buttonChild = (
      <React.Fragment>
        {icon ? (
          labelElement ? (
            <Icon fontSize="inherit" icon={icon} />
          ) : (
            <FabIcon icon={icon} />
          )
        ) : null}
        {labelElement}
        {endIcon ? <Icon icon={endIcon} /> : null}
      </React.Fragment>
    );

    if (fab || (icon && !labelElement)) {
      const { size, ...fabProps } = otherProps as FabProps;

      return (
        <RootFab
          color={color}
          ref={buttonRef}
          size={customSize === "tiny" ? "small" : size}
          tabIndex={groupControlsFocus ? -1 : tabIndex}
          tiny={customSize === "tiny"}
          {...fabProps}
        >
          {buttonChild}
        </RootFab>
      );
    }

    return (
      <Tooltip
        isOverflowing={isOverflowing}
        placement="bottom"
        title={labelText}
      >
        <RootButton
          color={color === "default" && variant === "text" ? "dark" : color}
          fullWidth={fullWidth}
          onTouchCancel={onTouchFinish}
          onTouchEnd={onTouchFinish}
          onTouchStart={() => (touchStart.current = Date.now())}
          ref={buttonRef}
          tabIndex={groupControlsFocus ? -1 : tabIndex}
          variant={variant}
          {...otherProps}
        >
          {buttonChild}
        </RootButton>
      </Tooltip>
    );
  }
);

export default Button;
